/**
 * W1-11b (app-shell-ui): saving files from the browser (recovery data, error reports).
 */

/** Offers a Blob as a download with the given file name. */
export function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30_000);
}

/** File extension for a recovery blob by its type. */
export function extensionFor(type: string): string {
  if (type.includes('zip')) return 'zip';
  if (type.includes('json')) return 'json';
  if (type.startsWith('text/')) return 'txt';
  return 'bin';
}

/** "2026-10-05_14-03" (local time) for file names. */
export function fileStamp(d: Date = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}-${p(d.getMinutes())}`;
}

/** Labels of the error report (translated by the caller; English defaults for start-up failures). */
export interface ReportLabels {
  componentStack(): string;
  url(url: string): string;
  browser(agent: string): string;
  time(iso: string): string;
}

const ENGLISH_LABELS: ReportLabels = {
  componentStack: () => 'Component stack:',
  url: (url) => `Address: ${url}`,
  browser: (agent) => `Browser: ${agent}`,
  time: (iso) => `Time: ${iso}`,
};

/** Plain-text error report (crash screens, "Copy error details"). */
export function errorReport(
  error: unknown,
  componentStack: string | null,
  labels: ReportLabels = ENGLISH_LABELS,
): string {
  const e = error instanceof Error ? error : null;
  const head = e === null ? String(error) : `${e.name}: ${e.message}`;
  const lines = [head];
  if (e?.stack !== undefined) lines.push('', e.stack);
  if (componentStack !== null && componentStack.trim() !== '') {
    lines.push('', labels.componentStack(), componentStack.trim());
  }
  if (typeof location !== 'undefined') lines.push('', labels.url(location.href));
  if (typeof navigator !== 'undefined') lines.push(labels.browser(navigator.userAgent));
  lines.push(labels.time(new Date().toISOString()));
  return lines.join('\n');
}

/** Short one-line text of an error for toasts. */
export function errorText(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message !== '') return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const msg = (error as { readonly message: unknown }).message;
    if (typeof msg === 'string' && msg !== '') return msg;
  }
  if (typeof error === 'string' && error !== '') return error;
  return fallback;
}
